/**
 * AddAlternateModal (m-alt) — Add an alternate product for an existing line item.
 *
 * Spec: Different product, sub-area + sq ft inherited from parent, price diff preview.
 * The alternate inherits the parent's sub-area and sq ft (not blank).
 */

import React, { useState, useMemo } from 'react';
import Slideover from '../../../ui/Slideover';
import { Button } from '../../../ui/Button';
import { Input } from '../../../ui/Input';
import SelectMenu from '../../../ui/SelectMenu';
import {
  PRODUCT_CATEGORIES,
  getCategoryMargin,
  synthesizeTenantProduct,
  disambiguateProductLabels,
  titleCaseCategoryId,
  isPerSqftCategory,
} from '../../../../quote-builder/seedCatalog';
import { coverageOptions, surfaceForSubArea, isOfferableOnSurface } from '../../../../quote-builder/productCoverage';

import { lineTotal as calcLineTotal, alternatePriceDiff, formatMarginPct } from '../../../../quote-builder';
import { displayRValue } from '../../../../quote-builder/materialList';

// IDI-309: no seed category margin. Price an alternate at the dealer's
// category margin when set, else at the parent line's resolved margin.
function altMargin(categoryId, parentComputed, tenantCategoryMargins) {
  const dealer = getCategoryMargin(categoryId, tenantCategoryMargins);
  if (dealer != null) return dealer;
  const m = Number(parentComputed?.margin);
  return Number.isFinite(m) && m >= 0 && m < 1 ? m : 0;
}

// IDI-507 (10): a pricebook product's rValue is already "R-30" (a seed one is
// 30), so prefixing "R-" here showed "(R-R-30)". displayRValue prefixes only a
// bare number.
export function alternateProductLabel(p) {
  const r = p && p.rValue ? displayRValue({ rValue: p.rValue }) : '';
  return `${p.label}${r ? ` (${r})` : ''}`;
}

// IDI-136 (D5): the alternate's costs. Material follows buildLineItem's
// material branches exactly (per-sq-ft category -> sqft x rate; coverage ->
// sqft / coverage x cost per unit; unset price -> 0). Labor is the parent's
// labor per sq ft x the alternate's sq ft: for catalog rows labor depends on
// the sub-area and sq ft, not the product (resolveLaborCost), so that is what
// Add Item would charge here.
function alternateCosts(product, coverageSqftPerUnit, sqft, parentItem) {
  const costPerUnit = Number(product?.materialCostPerUnit) || 0;
  const coverage = Number(coverageSqftPerUnit) > 0 ? Number(coverageSqftPerUnit) : (Number(product?.squareFeetPerUnit) || 0);
  let materialCost = 0;
  if (costPerUnit > 0) {
    if (isPerSqftCategory(product.categoryId)) materialCost = Math.round(costPerUnit * sqft);
    else if (coverage > 1) materialCost = Math.round((sqft / coverage) * costPerUnit);
  }
  const parentSqft = Number(parentItem?.sqft) || 0;
  const parentLabor = Number(parentItem?.laborCost) || 0;
  const laborCost = parentSqft > 0 ? Math.round((parentLabor / parentSqft) * sqft) : 0;
  return { materialCost, laborCost, totalCost: materialCost + laborCost };
}

export default function AddAlternateModal({ open, onClose, parentItem, parentComputed, onAdd, tenantCategoryMargins = null, tenantQuoteItems = [] }) {
  const [categoryId, setCategoryId] = useState('');
  const [productId, setProductId] = useState('');
  const [coverageChoice, setCoverageChoice] = useState('');
  const [sqft, setSqft] = useState('');

  // DEF-033 fix: useEffect (not useState) so sqft re-syncs when parentItem
  // changes (e.g., modal opens with a new parent after being mounted).
  // Also reset category/product so stale selections don't carry over.
  React.useEffect(() => {
    if (parentItem?.sqft) {
      setSqft(String(parentItem.sqft));
    } else {
      setSqft('');
    }
    setCategoryId('');
    setProductId('');
    setCoverageChoice('');
  }, [parentItem]);

  // IDI-136 (D5): Add Item's categories, rows and coverage for this sub-area.
  // The builder's own catalog is the only product source (no built-ins).
  const rows = useMemo(() => (Array.isArray(tenantQuoteItems) ? tenantQuoteItems : []).filter((r) => r && r.sku != null), [tenantQuoteItems]);
  const surface = parentItem?.coverageSurface || surfaceForSubArea(parentItem?.areaId, parentItem?.subAreaLabel);
  const categoryOptions = useMemo(() => {
    const ids = Array.from(new Set(rows.map((r) => r.categoryId).filter((c) => c != null).map(String)))
      .filter((cid) => cid !== 'accessories');
    const staticIds = new Set(PRODUCT_CATEGORIES.map((c) => String(c.id)));
    return [
      ...PRODUCT_CATEGORIES.filter((c) => ids.includes(String(c.id))).map((c) => ({ value: String(c.id), label: c.label })),
      ...ids.filter((cid) => !staticIds.has(cid)).map((cid) => ({ value: cid, label: titleCaseCategoryId(cid) })),
    ];
  }, [rows]);
  const products = useMemo(() => {
    if (!categoryId) return [];
    const inCat = rows.filter((r) => String(r.categoryId) === String(categoryId)).map((r) => synthesizeTenantProduct(r)).filter(Boolean);
    return disambiguateProductLabels(inCat).filter((p) => isOfferableOnSurface(p, surface));
  }, [rows, categoryId, surface]);
  const selectedProduct = useMemo(() => products.find((p) => String(p.id) === String(productId)) || null, [products, productId]);
  const coverageOpts = useMemo(() => coverageOptions(selectedProduct, surface), [selectedProduct, surface]);
  const chosenCoverage = coverageOpts.find((o) => o.value === coverageChoice) || (coverageOpts.length === 1 ? coverageOpts[0] : null);
  const coverageReady = coverageOpts.length === 0 || Boolean(chosenCoverage);

  // Calculate alternate cost and price diff
  const altPreview = useMemo(() => {
    if (!selectedProduct || !sqft || !coverageReady) return null;
    const costs = alternateCosts(selectedProduct, chosenCoverage?.sqftPerUnit, parseFloat(sqft), parentItem);
    // IDI-309: no seed category margin; price the alternate at the parent's
    // resolved margin unless the dealer set one for this category.
    const margin = altMargin(categoryId, parentComputed, tenantCategoryMargins);
    const total = calcLineTotal(costs.totalCost, margin);
    const diff = parentComputed ? alternatePriceDiff(parentComputed.total, total) : 0;
    return { costs, margin, total, diff };
  }, [selectedProduct, sqft, coverageReady, chosenCoverage, parentItem, categoryId, parentComputed, tenantCategoryMargins]);

  const handleSubmit = () => {
    if (!selectedProduct || !sqft || !coverageReady) return;
    const costs = alternateCosts(selectedProduct, chosenCoverage?.sqftPerUnit, parseFloat(sqft), parentItem);
    const margin = altMargin(categoryId, parentComputed, tenantCategoryMargins);
    const total = calcLineTotal(costs.totalCost, margin);

    onAdd(parentItem.id, {
      id: `alt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      productId,
      productLabel: selectedProduct.label,
      categoryId,
      subAreaId: parentItem.subAreaId,
      subAreaLabel: parentItem.subAreaLabel,
      sqft: parseFloat(sqft),
      materialCost: costs.materialCost,
      laborCost: costs.laborCost,
      cost: costs.totalCost,
      margin,
      total,
      rValue: chosenCoverage?.rValue || selectedProduct.rValue || null,
    });
    onClose();
  };

  const footer = (
    <div className="flex gap-2 justify-end w-full">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={handleSubmit} disabled={!selectedProduct || !coverageReady || !parseFloat(sqft)}>Add Alternate</Button>
    </div>
  );

  return (
    <Slideover open={open} onClose={onClose} title="Add Alternate" subtitle={`For: ${parentItem?.productLabel}`} footer={footer} width="sm">
      <div className="space-y-4">
        {/* Inherited info */}
        <div className="card p-3 text-xs" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--neutral-50)' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            <strong>Inherits from parent:</strong> {parentItem?.subAreaLabel || 'No sub-area'} · {parentItem?.sqft} {parentItem?.unit || 'sq ft'}
          </p>
        </div>

        {/* Category */}
        <div>
          <label className="label mb-1">Product Category</label>
          <SelectMenu
            value={categoryId}
            onChange={(val) => { setCategoryId(val); setProductId(''); setCoverageChoice(''); }}
            aria-label="Alternate category"
            options={categoryOptions}  /* PT-15 #9: Accessories stay hidden. IDI-136: only categories with a catalog row. */
            placeholder="Select category…"
          />
        </div>

        {/* Product */}
        {categoryId && (
          <div>
            <label className="label mb-1">Product</label>
            <SelectMenu
              value={productId}
              aria-label="Alternate product"
              onChange={(val) => { setProductId(val); setCoverageChoice(''); }}
              options={products.map((p) => ({ value: String(p.id), label: alternateProductLabel(p) }))}
              placeholder="Select product…"
            />
          </div>
        )}

        {/* IDI-136 (D5): Coverage, as in Add Item, on the parent's surface. */}
        {selectedProduct && coverageOpts.length > 1 && (
          <div>
            <label className="label mb-1">Coverage{surface === 'wall' ? ' (wall)' : ''}</label>
            <SelectMenu
              aria-label="Alternate coverage"
              value={coverageChoice}
              onChange={setCoverageChoice}
              options={coverageOpts.map((o) => ({ value: o.value, label: o.label }))}
              placeholder="Select coverage…"
            />
          </div>
        )}

        {/* Square footage (inherited, editable) */}
        <Input
          label="Square Footage"
          type="number"
          min="1"
          value={sqft}
          onChange={(e) => setSqft(e.target.value)}
        />

        {/* Price diff preview */}
        {altPreview && (
          <div className="card p-3 space-y-1" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
              Alternate total: <strong>${(altPreview.total / 100).toFixed(2)}</strong>
            </p>
            <p className="text-sm" style={{ color: altPreview.diff < 0 ? 'var(--green-700)' : altPreview.diff > 0 ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
              {altPreview.diff < 0 ? '↓' : altPreview.diff > 0 ? '↑' : '='} ${Math.abs(altPreview.diff / 100).toFixed(2)} {altPreview.diff < 0 ? 'less' : altPreview.diff > 0 ? 'more' : 'same'} than original
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              Margin: {formatMarginPct(altPreview.margin)}
            </p>
          </div>
        )}
      </div>
    </Slideover>
  );
}
